import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import slugify from '../utils/slugify';
import { API_BASE_URL } from '../config/backendConfig';

/** Heritage destination search shared by mobile and desktop headers (same API + navigation). */
export function useHeritageDestinationSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchSearchResults = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/heritage/search?q=${encodeURIComponent(query.trim())}`);
      const data = await res.json();
      setSearchResults(data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const scheduleFetchSearchResults = useCallback(
    (val) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => fetchSearchResults(val), 300);
    },
    [fetchSearchResults],
  );

  const handleSearchChange = useCallback(
    (e) => {
      const val = e.target.value;
      setSearchQuery(val);
      scheduleFetchSearchResults(val);
    },
    [scheduleFetchSearchResults],
  );

  const resetSearchState = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchLoading(false);
  }, []);

  const handleResultClick = useCallback(
    async (result) => {
      resetSearchState();

      if (result.source === 'heritage') {
        navigate(`/destinations/heritage/${slugify(result.name) || encodeURIComponent(result.name)}`);
        return;
      }

      try {
        const sourceParam = result.source === 'international' ? '&source=international' : '';
        const res = await fetch(
          `${API_BASE_URL}/heritage/destination/${encodeURIComponent(result.name)}?${sourceParam}`,
        );
        const data = await res.json();
        if (data.found && data.placeData) {
          const destName = data.placeData?.hero?.title || data.placeData?.place?.name || result.name;
          navigate(`/destinations/${slugify(destName) || 'destination'}`, {
            state: { placeData: data.placeData, category: data.category },
          });
          return;
        }
      } catch {
        /* fall through */
      }

      navigate(`/destinations/heritage/${slugify(result.name) || encodeURIComponent(result.name)}`);
    },
    [navigate, resetSearchState],
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    fetchSearchResults,
    scheduleFetchSearchResults,
    handleSearchChange,
    handleResultClick,
    resetSearchState,
  };
}

/** Same visibility rule as the YovoTripHeader search dropdown */
export function shouldShowHeritageSearchDropdown(searchOpen, searchQuery, searchLoading, searchResults) {
  return (
    searchOpen &&
    (searchResults.length > 0 || (searchQuery.length >= 2 && !searchLoading))
  );
}
